import React, { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";
import _ from "lodash";
import useSettings from "../../../hooks/useSettings";
import PaginationForItems from "../../common/PaginationForItems";
import { Header } from "../../internal/Header/Header";
import Footer from "../../pages/NewLandingPage/components/Footer";
import { Images } from "../../../assets/images";
import theme from "../../../assets/theme";
import ProfileModal from "../ProfilePage";

function ProfileScreen(props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [experience, setExperience] = useState("");
  const [location, setLocation] = useState("");
  const [language, setLanguage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [modal, setModal] = useState({ state: false });

  const filterRef = useRef({
    experience: "",
    location: "",
    language: "",
  });

  const { setting, settingsLoading } = useSettings();
  const profilesPerPage = 10;

  // Experience options
  const experienceSelections = [
    { label: "All Experience", value: "" },
    { label: "More than 10 years", value: 10 },
    { label: "More than 20 years", value: 20 },
    { label: "More than 30 years", value: 30 },
    { label: "More than 40 years", value: 40 },
  ];

  // Get data based on neutral type
  const data = useMemo(() => {
    if (props.neutral === "arbitrators") {
      return setting?.arbitratorsInfo || [];
    } else if (props.neutral === "mediators") {
      return setting?.mediatorsInfo || [];
    }
    return [];
  }, [props.neutral, setting]);

  // Extract unique locations from data
  const locationSelections = useMemo(() => {
    if (!data || data.length === 0) return [];
    const locations = data
      .map((profile) => profile.location)
      .filter((location) => location && location.trim() !== "");
    return ["", ..._.uniq(locations).sort()];
  }, [data]);

  // Extract unique languages from data (handling array format)
  const normalize = (str) => {
    return str
      ?.trim()
      .replace(/[.,]+$/g, "") // remove trailing dots/commas
      .toLowerCase();
  };

  const toTitleCase = (str) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
  };

  const languageSelections = useMemo(() => {
    if (!data || data.length === 0) return [];

    let normalizedMap = new Map();

    data.forEach((profile) => {
      const languages = Array.isArray(profile.languagesKnown)
        ? profile.languagesKnown
        : profile.languagesKnown
        ? [profile.languagesKnown]
        : [];

      languages.forEach((lang) => {
        const norm = normalize(lang);
        if (norm && norm !== "english") {
          normalizedMap.set(norm, toTitleCase(norm));
        }
      });
    });

    return ["", ...Array.from(normalizedMap.values()).sort()];
  }, [data]);

  const searchData = (profile, input) => {
    return (
      _.lowerCase(profile.name).includes(_.lowerCase(input)) ||
      _.lowerCase(profile.qualification).includes(_.lowerCase(input)) ||
      _.lowerCase(profile.location).includes(_.lowerCase(input)) ||
      _.lowerCase(profile.areasOfExpertise).includes(_.lowerCase(input))
    );
  };

  // Optimized filter function
  const applyFilters = (profile, filters) => {
    // Experience filter
    if (filters.experience) {
      const requiredYears = parseInt(filters.experience);
      if (profile.yearsOfExperience < requiredYears) {
        return false;
      }
    }

    // Location filter
    if (filters.location && filters.location.trim() !== "") {
      if (
        !profile.location ||
        profile.location.toLowerCase() !== filters.location.toLowerCase()
      ) {
        return false;
      }
    }

    // Language filter (handling array of languages)
    if (filters.language && filters.language.trim() !== "") {
      const selected = normalize(filters.language);

      const hasLanguage = Array.isArray(profile.languagesKnown)
        ? profile.languagesKnown.some((lang) => normalize(lang) === selected)
        : false;

      if (!hasLanguage) return false;
    }

    return true;
  };

  // Combined filter and search function
  const filterData = (query, filters) => {
    if (!data || data.length === 0) {
      setCurrentData([]);
      return;
    }

    const filtered = data.filter((profile) => {
      return searchData(profile, query) && applyFilters(profile, filters);
    });

    setCurrentData(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Initialize data when component mounts or data changes
  useEffect(() => {
    window.scrollTo(0, 0);
    if (data && data.length > 0) {
      setCurrentData(data);
    }
  }, [data, settingsLoading]);

  // Apply filters whenever any filter changes
  useEffect(() => {
    filterData(searchQuery, filterRef.current); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, experience, location, language, data]);

  // Pagination
  const indexOfLastProfile = currentPage * profilesPerPage;
  const indexOfFirstProfile = indexOfLastProfile - profilesPerPage;
  const currentProfiles = currentData.slice(
    indexOfFirstProfile,
    indexOfLastProfile
  );

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle search change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  // Handle experience change
  const handleExperienceChange = (e) => {
    const value = e.target.value;
    setExperience(value);
    filterRef.current = {
      ...filterRef.current,
      experience: value,
    };
  };

  // Handle location change
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    filterRef.current = {
      ...filterRef.current,
      location: value,
    };
  };

  // Handle language change
  const handleLanguageChange = (e) => {
    const value = e.target.value;
    setLanguage(value);
    filterRef.current = {
      ...filterRef.current,
      language: value,
    };
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
  };

  // Get placeholder image if image is missing
  const getProfileImage = (profile) => {
    if (profile.image && profile.image.trim() !== "") {
      return profile.image;
    }
    // Return a placeholder image
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      profile.name || "User"
    )}&size=400&background=00838c&color=fff&bold=true`;
  };

  return (
    <>
      <Header>
        <PageWrapper>
          <HeroSection
            image={
              props.neutral === "arbitrators"
                ? Images.arbitrator
                : Images.mediator
            }
          >
            <HeroOverlay />
            <HeroContent>
              <HeroTitle>
                {props.neutral === "arbitrators" ? "Arbitrators" : "Mediators"}
              </HeroTitle>
            </HeroContent>
          </HeroSection>

          <FiltersSection>
            <FiltersContainer>
              <FilterGroup>
                <FilterLabel>Search</FilterLabel>
                <SearchInputWrapper>
                  <SearchIcon>{"🔍"}</SearchIcon>
                  <SearchInput
                    type="text"
                    placeholder="Name, Expertise or Qualification"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  {searchQuery && (
                    <ClearButton onClick={clearSearch}>✕</ClearButton>
                  )}
                </SearchInputWrapper>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Experience</FilterLabel>
                <Select
                  style={{
                    fontFamily: theme.fonts.primaryFontSemiBold,
                    color: "#293461",
                  }}
                  value={experience}
                  onChange={handleExperienceChange}
                >
                  {experienceSelections.map((item, index) => (
                    <MenuOption key={index} value={item.value}>
                      {item.label}
                    </MenuOption>
                  ))}
                </Select>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Location</FilterLabel>
                <Select
                  style={{
                    width: "100%",
                    fontFamily: theme.fonts.primaryFontSemiBold,
                    color: "#293461",
                  }}
                  value={location}
                  onChange={handleLocationChange}
                >
                  <MenuOption value="">All Locations</MenuOption>
                  {locationSelections
                    .filter((loc) => loc !== "")
                    .map((item, index) => (
                      <MenuOption key={index} value={item}>
                        {item}
                      </MenuOption>
                    ))}
                </Select>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Languages Known</FilterLabel>
                <Select
                  style={{
                    fontFamily: theme.fonts.primaryFontSemiBold,
                    color: "#293461",
                  }}
                  value={language}
                  onChange={handleLanguageChange}
                >
                  <MenuOption value="">All Languages</MenuOption>
                  {languageSelections
                    .filter((lang) => lang !== "")
                    .map((item, index) => (
                      <MenuOption key={index} value={item}>
                        {item}
                      </MenuOption>
                    ))}
                </Select>
              </FilterGroup>
            </FiltersContainer>

            {/* Results count */}
            <ResultsCount>
              Showing {currentData.length}{" "}
              {currentData.length === 1 ? "result" : "results"}
              {(searchQuery || experience || location || language) && (
                <ClearFiltersButton
                  onClick={() => {
                    setSearchQuery("");
                    setExperience("");
                    setLocation("");
                    setLanguage("");
                    filterRef.current = {
                      experience: "",
                      location: "",
                      language: "",
                    };
                  }}
                >
                  Clear All Filters
                </ClearFiltersButton>
              )}
            </ResultsCount>
          </FiltersSection>

          {/* Arbitrators Grid */}
          {!settingsLoading ? (
            <ArbitratorsSection>
              {currentProfiles && currentProfiles.length > 0 ? (
                <ArbitratorsGrid>
                  {currentProfiles.map((arbitrator, index) => (
                    <ArbitratorCard key={index}>
                      <CardImageWrapper>
                        <CardImage
                          src={getProfileImage(arbitrator)}
                          alt={arbitrator.name || "Profile"}
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              arbitrator.name || "User"
                            )}&size=400&background=00838c&color=fff&bold=true`;
                          }}
                        />
                      </CardImageWrapper>
                      <CardContent>
                        <ArbitratorName>
                          {arbitrator.name || "Unknown"}
                        </ArbitratorName>
                        <ArbitratorExperience>
                          {arbitrator.yearsOfExperience || 0} years Experience
                        </ArbitratorExperience>
                        {arbitrator.location && (
                          <LocationTag>
                            {"📍"} {arbitrator.location}
                          </LocationTag>
                        )}
                        {arbitrator.languagesKnown &&
                          arbitrator.languagesKnown.length > 0 && (
                            <LanguageTags>
                              {(Array.isArray(arbitrator?.languagesKnown)
                                ? arbitrator.languagesKnown
                                : (arbitrator?.languagesKnown || "").split(",")
                              )
                                .slice(0, 3)
                                .map((lang, idx) => (
                                  <LanguageTag key={idx}>
                                    {lang.trim()}
                                  </LanguageTag>
                                ))}

                              {arbitrator.languagesKnown.length > 3 && (
                                <LanguageTag>
                                  +{arbitrator.languagesKnown.length - 3} more
                                </LanguageTag>
                              )}
                            </LanguageTags>
                          )}
                        <ArbitratorDescription>
                          {arbitrator.areasOfExpertise ||
                            "No expertise information available"}
                        </ArbitratorDescription>
                      </CardContent>
                      <KnowMoreButton
                        // onClick={() =>
                        //   navigate(`/neutral/${props.neutral}/${arbitrator._id}`)
                        // }
                        onClick={() =>
                          setModal({ state: true, metaData: arbitrator })
                        }
                      >
                        Know More
                      </KnowMoreButton>
                      {/* <PrimaryCTAButton> Know More</PrimaryCTAButton> */}
                    </ArbitratorCard>
                  ))}
                </ArbitratorsGrid>
              ) : (
                <NoResults>
                  <NoResultsIcon>{"🔍"}</NoResultsIcon>
                  <NoResultsTitle>No Results Found</NoResultsTitle>
                </NoResults>
              )}
            </ArbitratorsSection>
          ) : (
            <LoadingSection>
              <Spinner />
              <LoadingText>Loading profiles...</LoadingText>
            </LoadingSection>
          )}

          {/* Pagination */}
          {!settingsLoading && currentData.length > 0 && (
            <PaginationForItems
              profilesPerPage={profilesPerPage}
              totalProfiles={currentData.length}
              paginate={paginate}
              page={currentPage}
            />
          )}
        </PageWrapper>
      </Header>
      <Footer />

      {modal?.state && <ProfileModal {...{ modal, setModal }} />}
    </>
  );
}

export default ProfileScreen;

/* Styled Components */

const PageWrapper = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color: #333;
  background: #f8f9fa;
  min-height: 100vh;
`;

const HeroSection = styled.div`
  position: relative;
  height: 400px;
  background: ${({ image }) => `url(${image})`}; 
  center/cover;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  color: white;
  font-weight: 700;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const FiltersSection = styled.div`
  background: white;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FiltersContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-weight: 600;
  color: #949ab0;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 0.9rem;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 1rem;
  color: #999;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.8rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border 0.3s;
  color: #293461;
  font-family: openSans-SemiBold, sans-serif;

  &:focus {
    outline: none;
    border-color: #00838c;
  }

  &::placeholder {
    color: #999;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0;
  display: flex;
  align-items: center;

  &:hover {
    color: #333;
  }
`;

const Select = styled.select`
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  background: white;
  transition: border 0.3s;

  &:focus {
    outline: none;
    border-color: #00838c;
  }
`;

const ResultsCount = styled.div`
  max-width: 1400px;
  margin: 1.5rem auto 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #666;
  font-size: 0.95rem;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const ClearFiltersButton = styled.button`
  background: #f0f0f0;
  color: #333;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.3s;

  &:hover {
    background: #e0e0e0;
  }
`;

const ArbitratorsSection = styled.section`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const ArbitratorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ArbitratorCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

// export const CardImageWrapper = styled.div`
//   width: 100%;
//   height: 280px;
//   overflow: hidden;
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   background: linear-gradient(90deg, #000 0%, #222 100%);
// `;

export const CardImageWrapper = styled.div`
  width: 100%;
  height: 280px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
`;



// export const CardImage = styled.img`
//   width: 100%;
//   height: 100%;
//   object-fit: cover; /* fills completely, may crop a bit */
//   object-position: center;
//   transition: transform 0.3s;

//   ${ArbitratorCard}:hover & {
//     transform: scale(1.05);
//   }
// `;

export const CardImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;  
  object-position: center;
  transition: none;      
`;


const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ArbitratorName = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ArbitratorExperience = styled.p`
  color: #00838c;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 500;
`;

const LocationTag = styled.div`
  color: #666;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const LanguageTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0;
`;

const LanguageTag = styled.span`
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ArbitratorDescription = styled.p`
  color: #666;
  line-height: 1.6;
  font-size: 0.95rem;
  margin: 0.5rem 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const KnowMoreButton = styled.button`
  background: #00838c;
  color: white;
  border: none;
  padding: 1rem;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background 0.3s;
  font-size: 1rem;

  &:hover {
    background: #006064;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 4rem 2rem;
`;

const NoResultsIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const NoResultsTitle = styled.h3`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const LoadingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  min-height: 400px;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #00838c;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  margin-top: 1rem;
  color: #666;
  font-size: 1rem;
`;

const MenuOption = styled.option`
  color: #293461;
  font-family: openSans-SemiBold, sans-serif;
`;
